/**
 * Cloudflare R2 Storage Settings - Super Admin
 * Configure R2 bucket for image/file storage
 */
import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Cloud, Save, Loader2, Eye, EyeOff, TestTube2, 
  CheckCircle2, XCircle, HardDrive, Upload, Link2,
  Key, Server, FolderOpen, Globe, Shield
} from 'lucide-react';

export default function CloudflareR2Settings({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [settings, setSettings] = useState({
    r2_enabled: false,
    r2_account_id: '',
    r2_access_key_id: '',
    r2_secret_access_key: '',
    r2_bucket_name: '',
    r2_public_url: '',
    r2_region: 'auto'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/storage');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch R2 settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/storage', settings);
      toast.success('Cloudflare R2 settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post('/settings/storage/test', settings);
      setTestResult({ success: true, message: response.data.message });
      toast.success('Connection successful!');
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.response?.data?.detail || 'Connection failed' 
      });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="r2-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Cloud className="h-6 w-6 text-orange-500" />
            Cloudflare R2 Storage
          </h1>
          <p className="text-gray-500 text-sm mt-1">Configure cloud storage for images and files</p>
        </div>
        <Badge variant={settings.r2_enabled ? 'default' : 'secondary'} className={settings.r2_enabled ? 'bg-green-100 text-green-700' : ''}>
          {settings.r2_enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <HardDrive className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Why use Cloudflare R2?</p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>• Store images, logos, and documents in the cloud</li>
                <li>• Fast global CDN delivery for better performance</li>
                <li>• No egress fees - cost-effective storage</li>
                <li>• S3-compatible API for easy integration</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-orange-500" />
            R2 Credentials
          </CardTitle>
          <CardDescription>
            Get these from your Cloudflare dashboard → R2 → Manage R2 API Tokens
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Cloud className="h-5 w-5 text-gray-600" />
              <div>
                <p className="font-medium text-gray-800">Enable R2 Storage</p>
                <p className="text-sm text-gray-500">Use Cloudflare R2 for all file uploads</p>
              </div>
            </div>
            <Switch
              checked={settings.r2_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, r2_enabled: checked })}
              data-testid="r2-enable-toggle"
            />
          </div>

          <div className={`space-y-4 ${!settings.r2_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Account ID */}
            <div>
              <Label htmlFor="account_id" className="flex items-center gap-2">
                <Server className="h-4 w-4 text-gray-500" />
                Account ID
              </Label>
              <Input
                id="account_id"
                value={settings.r2_account_id}
                onChange={(e) => setSettings({ ...settings, r2_account_id: e.target.value })}
                placeholder="e.g., a1b2c3d4e5f6g7h8i9j0..."
                className="mt-1 font-mono text-sm"
                data-testid="r2-account-id"
              />
              <p className="text-xs text-gray-500 mt-1">Found in Cloudflare dashboard URL or R2 overview</p>
            </div>

            {/* Access Key ID */}
            <div>
              <Label htmlFor="access_key" className="flex items-center gap-2">
                <Key className="h-4 w-4 text-gray-500" />
                Access Key ID
              </Label>
              <Input
                id="access_key"
                value={settings.r2_access_key_id}
                onChange={(e) => setSettings({ ...settings, r2_access_key_id: e.target.value })}
                placeholder="e.g., AKIAIOSFODNN7EXAMPLE"
                className="mt-1 font-mono text-sm"
                data-testid="r2-access-key"
              />
            </div>

            {/* Secret Access Key */}
            <div>
              <Label htmlFor="secret_key" className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-gray-500" />
                Secret Access Key
              </Label>
              <div className="relative mt-1">
                <Input
                  id="secret_key"
                  type={showSecretKey ? 'text' : 'password'}
                  value={settings.r2_secret_access_key}
                  onChange={(e) => setSettings({ ...settings, r2_secret_access_key: e.target.value })}
                  placeholder="Enter your secret access key"
                  className="font-mono text-sm pr-10"
                  data-testid="r2-secret-key"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                >
                  {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Keep this secret! Never share publicly.</p>
            </div>

            {/* Bucket Name */}
            <div>
              <Label htmlFor="bucket" className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-gray-500" />
                Bucket Name
              </Label>
              <Input
                id="bucket"
                value={settings.r2_bucket_name}
                onChange={(e) => setSettings({ ...settings, r2_bucket_name: e.target.value })}
                placeholder="e.g., hotel-images"
                className="mt-1"
                data-testid="r2-bucket-name"
              />
              <p className="text-xs text-gray-500 mt-1">Create a bucket in R2 dashboard first</p>
            </div>

            {/* Public URL */}
            <div>
              <Label htmlFor="public_url" className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-500" />
                Public URL (Optional)
              </Label>
              <Input
                id="public_url"
                value={settings.r2_public_url}
                onChange={(e) => setSettings({ ...settings, r2_public_url: e.target.value })}
                placeholder="e.g., https://images.yourdomain.com"
                className="mt-1"
                data-testid="r2-public-url"
              />
              <p className="text-xs text-gray-500 mt-1">Custom domain for public access (configure in R2 → Settings → Public Access)</p>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg flex items-center gap-3 ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {testResult.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <span className={testResult.success ? 'text-green-700' : 'text-red-700'}>
                {testResult.message}
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              onClick={handleTestConnection}
              variant="outline"
              disabled={testing || !settings.r2_enabled || !settings.r2_account_id || !settings.r2_access_key_id || !settings.r2_secret_access_key || !settings.r2_bucket_name}
              data-testid="test-connection-btn"
            >
              {testing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing...</>
              ) : (
                <><TestTube2 className="h-4 w-4 mr-2" />Test Connection</>
              )}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-r2-settings-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Save Settings</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-600">
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">1</span>
            <div>
              <p className="font-medium text-gray-800">Create R2 Bucket</p>
              <p>Go to Cloudflare Dashboard → R2 → Create bucket</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">2</span>
            <div>
              <p className="font-medium text-gray-800">Create API Token</p>
              <p>R2 → Manage R2 API Tokens → Create API token → Select "Object Read & Write"</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">3</span>
            <div>
              <p className="font-medium text-gray-800">Configure Public Access (Optional)</p>
              <p>R2 → Your bucket → Settings → Public Access → Add custom domain</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">4</span>
            <div>
              <p className="font-medium text-gray-800">Enter Credentials Above</p>
              <p>Copy Account ID, Access Key ID, Secret Access Key, and Bucket Name</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
